import { Link } from '@tanstack/react-router';

import { NAV_ICONS } from '@/config/nav-icons';
import type { NavItem } from '@/config/navigation';
import { cn } from '@/lib/utils';

interface SidebarNavItemProps {
  readonly item: NavItem;
  readonly isActive: boolean;
  /** How many rows are waiting on this screen. Absent until the count arrives. */
  readonly badge?: number;
  /** Set by the mobile drawer, which dismisses itself on the way out. */
  readonly onNavigate?: () => void;
}

export function SidebarNavItem({ item, isActive, badge, onNavigate }: SidebarNavItemProps) {
  const Icon = NAV_ICONS[item.id];

  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      {...(badge === undefined || badge === 0 ? {} : { title: `${item.label} — ${String(badge)} waiting` })}
      // aria-current is what tells a screen reader which page it is on; the
      // background colour alone communicates nothing.
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13.5px] leading-none no-underline transition-colors',
        isActive
          ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
          : 'font-normal text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          'size-[15px] shrink-0',
          isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/50',
        )}
      />
      <span className="truncate">{item.label}</span>

      {/*
        Both markers stay out of the accessible name. A link called "Food
        catalog 42" is a link whose name changes every time the queue does,
        and every lookup in this codebase — tests included — addresses these
        by their label. The count is on the `title` instead, and it is a
        prompt to click rather than information that is only here.
      */}
      {badge === undefined || badge === 0 ? null : (
        <span
          aria-hidden="true"
          className="ml-auto shrink-0 rounded-md bg-err-bg px-1.5 py-0.5 font-mono text-[11px] leading-none text-err-fg"
        >
          {badge.toLocaleString('en-IN')}
        </span>
      )}

      {item.isNew === true && (badge === undefined || badge === 0) ? (
        <span
          aria-hidden="true"
          className="ml-auto shrink-0 rounded-md border border-dashed border-sidebar-foreground/30 px-1.5 py-0.5 text-[10.5px] leading-none text-sidebar-foreground/55"
        >
          new
        </span>
      ) : null}
    </Link>
  );
}
